<div class="relative min-h-screen bg-slate-50 -m-4 sm:-m-8">

  @if (data()?.loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/70 backdrop-blur-[1px]"
         role="status" aria-live="polite" aria-busy="true">
      <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg">
        <i class="pi pi-spin pi-spinner text-3xl text-teal-600"></i>
        <div class="text-center">
          <p class="text-sm font-semibold text-slate-800">Cargando forecast…</p>
          <p class="mt-0.5 text-xs text-slate-500">Comparando {{ yearA() }} vs {{ yearB() }}</p>
        </div>
      </div>
    </div>
  }

  <!-- Header -->
  <div class="bg-white border-b border-slate-200 px-4 py-3">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-8 h-8 rounded-lg bg-teal-600">
            <i class="pi pi-chart-bar text-white text-sm"></i>
          </span>
          <div>
            <h1 class="text-lg font-bold text-slate-900 leading-tight">Forecast de ingresos de caja</h1>
            <p class="text-[0.7rem] text-slate-500">Comparación año vs año · v2</p>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <div class="flex items-center gap-1.5"
             [class.pointer-events-none]="data()?.loading"
             [class.opacity-60]="data()?.loading">
          <label class="text-[0.7rem] font-semibold text-slate-500" for="year-a">Año A</label>
          <select id="year-a"
            class="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            [ngModel]="yearA()"
            (ngModelChange)="setYearA($event)"
            [disabled]="!!data()?.loading">
            @for (y of yearOptions(); track y) {
              <option [ngValue]="y" [disabled]="y >= yearB()">{{ y }}</option>
            }
          </select>

          <span class="text-slate-400 text-xs font-medium">vs</span>

          <label class="text-[0.7rem] font-semibold text-slate-500" for="year-b">Año B</label>
          <select id="year-b"
            class="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            [ngModel]="yearB()"
            (ngModelChange)="setYearB($event)"
            [disabled]="!!data()?.loading">
            @for (y of yearOptions(); track y) {
              <option [ngValue]="y" [disabled]="y <= yearA()">{{ y }}</option>
            }
          </select>
        </div>

        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>
      </div>
    </div>
  </div>

  <!-- Idea strip -->
  <div class="bg-teal-700 text-white px-4 py-2 text-xs">
    <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
      <span class="flex items-start gap-2 max-w-4xl">
        <i class="pi pi-info-circle mt-0.5 opacity-80"></i>
        <span>
          Compara <strong>años calendario completos</strong> (ene–dic).
          La gráfica de ingresos muestra solo <strong>pagos realmente aplicados</strong>
          (sin proyectar suscripciones).
        </span>
      </span>
    </div>
  </div>

  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <div class="p-4 space-y-3">

    <!-- KPIs (mes calendario actual / siguiente; no dependen de yearA/yearB) -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <button type="button"
        class="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-3 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
        (click)="openCardDetail('CURRENT_MONTH_INCOME')">
        <div class="flex items-center justify-between">
          <p class="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1 leading-tight">
            Ingresos de {{ data()?.cards?.currentMonthLabel || '—' }}
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.currentMonthIncome"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"
              (click)="$event.stopPropagation(); $event.preventDefault()"></i>
          </p>
          <span class="w-7 h-7 rounded-md bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
            <i class="pi pi-wallet text-xs"></i>
          </span>
        </div>
        <p class="mt-1.5 text-lg font-bold text-slate-900 tabular-nums leading-none">
          {{ (data()?.cards?.currentMonthIncome ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-teal-700 font-medium">
          {{ data()?.cards?.currentMonthEventCount ?? 0 }}
          {{ (data()?.cards?.currentMonthEventCount ?? 0) === 1 ? 'pago cobrado' : 'pagos cobrados' }}
        </p>
      </button>

      <button type="button"
        class="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-3 hover:border-sky-300 hover:shadow-md transition-all cursor-pointer"
        (click)="openCardDetail('CURRENT_MONTH_PENDING')">
        <div class="flex items-center justify-between">
          <p class="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1 leading-tight">
            Pagos pendientes del mes
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.pending"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"
              (click)="$event.stopPropagation(); $event.preventDefault()"></i>
          </p>
          <span class="w-7 h-7 rounded-md bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
            <i class="pi pi-clock text-xs"></i>
          </span>
        </div>
        <p class="mt-1.5 text-lg font-bold text-slate-900 tabular-nums leading-none">
          {{ (data()?.cards?.currentMonthPending ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-sky-700 font-medium">
          {{ data()?.cards?.currentMonthPendingCount ?? 0 }}
          {{ (data()?.cards?.currentMonthPendingCount ?? 0) === 1 ? 'recibo sin pagar' : 'recibos sin pagar' }}
        </p>
      </button>

      <button type="button"
        class="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-3 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer"
        (click)="openCardDetail('OVERDUE')">
        <div class="flex items-center justify-between">
          <p class="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1 leading-tight">
            Pagos Vencidos
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.overdue"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"
              (click)="$event.stopPropagation(); $event.preventDefault()"></i>
          </p>
          <span class="w-7 h-7 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <i class="pi pi-exclamation-triangle text-xs"></i>
          </span>
        </div>
        <p class="mt-1.5 text-lg font-bold text-slate-900 tabular-nums leading-none">
          {{ (data()?.cards?.currentMonthOverdue ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-amber-700 font-medium">
          {{ data()?.cards?.currentMonthOverdueCount ?? 0 }}
          {{ (data()?.cards?.currentMonthOverdueCount ?? 0) === 1 ? 'recibo vencido' : 'recibos vencidos' }}
        </p>
      </button>

      <div
        class="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-3 hover:border-emerald-300 hover:shadow-md transition-all">
        <div class="flex items-start justify-between gap-2">
          <p class="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1 leading-tight shrink-0">
            Proyección
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.nextMonth"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </p>
          <div class="flex items-center gap-1 min-w-0"
               [class.pointer-events-none]="data()?.loading"
               [class.opacity-60]="data()?.loading">
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="prevProjectionPeriod()"
              [disabled]="!canPrevProjectionPeriod() || !!data()?.loading"
              aria-label="Mes de proyección anterior">
              <i class="pi pi-chevron-left text-xs"></i>
            </button>
            <select
              class="min-w-0 max-w-[7.5rem] rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 capitalize focus:outline-none focus:ring-2 focus:ring-teal-500"
              [ngModel]="projectionPeriodValue()"
              (ngModelChange)="onProjectionPeriodValueChange($event)"
              [disabled]="!!data()?.loading"
              aria-label="Seleccionar mes de proyección">
              @for (opt of projectionPeriodOptions(); track opt.value) {
                <option [ngValue]="opt.value">{{ opt.label }}</option>
              }
            </select>
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="nextProjectionPeriod()"
              [disabled]="!canNextProjectionPeriod() || !!data()?.loading"
              aria-label="Mes de proyección siguiente">
              <i class="pi pi-chevron-right text-xs"></i>
            </button>
          </div>
        </div>
        <button type="button"
          class="mt-2 w-full text-left cursor-pointer rounded-lg -mx-1 px-1 py-0.5 hover:bg-emerald-50/60"
          (click)="openCardDetail('NEXT_MONTH_PROJECTION')">
          <p class="text-lg font-bold text-slate-900 tabular-nums leading-none inline-flex items-center gap-2">
            @if (projectionBusy()) {
              <i class="pi pi-spin pi-spinner text-slate-400 text-lg"></i>
            }
            {{ (projectionTotal() ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </p>
          <p class="mt-1 text-xs text-emerald-700 font-medium">
            {{ projectionCount() ?? 0 }}
            {{ (projectionCount() ?? 0) === 1 ? 'cobro proyectado' : 'cobros proyectados' }}
          </p>
        </button>
      </div>
    </div>

    <!-- Ingresos de caja: fila completa -->
    <div class="chart-panel cashflow-panel bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-col h-[24rem]">
      <div class="flex items-center justify-between mb-3 shrink-0">
        <div>
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Ingresos de caja
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.cashflow"
              tooltipPosition="right"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500">
            Pagos cobrados, sin IVA · {{ data()?.yearA ?? yearA() }} vs {{ data()?.yearB ?? yearB() }}
          </p>
        </div>
        @if (data()?.loading) {
          <i class="pi pi-spin pi-spinner text-slate-400"></i>
        }
      </div>
      <div class="chart-area relative flex-1 min-h-0">
        <p-chart type="bar"
          class="absolute inset-0 block"
          height="100%"
          [data]="cashflowChart()"
          [options]="cashflowOptions"
          [plugins]="cashflowPlugins"
          styleClass="h-full w-full" />
      </div>
    </div>

    <!-- Por producto + Por forma de pago (50/50) -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:items-stretch">
      <div class="chart-panel bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-col">
        <div class="flex items-start justify-between gap-2 mb-1 shrink-0">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Por producto
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.product"
              tooltipPosition="right"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <div class="flex items-center gap-1 shrink-0"
               [class.pointer-events-none]="data()?.loading"
               [class.opacity-60]="data()?.loading">
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="prevProductPeriod()"
              [disabled]="!!data()?.loading || !canPrevProductPeriod()"
              aria-label="Periodo anterior">
              <i class="pi pi-chevron-left text-xs"></i>
            </button>
            <select
              class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 capitalize focus:outline-none focus:ring-2 focus:ring-teal-500"
              [ngModel]="productPeriodValue()"
              (ngModelChange)="onProductPeriodValueChange($event)"
              [disabled]="!!data()?.loading"
              aria-label="Seleccionar mes y año">
              @for (opt of productPeriodOptions(); track opt.value) {
                <option [ngValue]="opt.value">{{ opt.label }}</option>
              }
            </select>
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="nextProductPeriod()"
              [disabled]="!!data()?.loading || !canNextProductPeriod()"
              aria-label="Periodo siguiente">
              <i class="pi pi-chevron-right text-xs"></i>
            </button>
          </div>
        </div>
        <div class="flex items-stretch gap-2 min-h-0 flex-1 mt-2">
          <div class="chart-area relative shrink-0 w-24 h-24 self-center">
            <p-chart type="doughnut"
              [data]="productChart()"
              [options]="productDoughnutOptions"
              class="block h-full w-full" />
          </div>
          <div class="min-w-0 flex-1 flex flex-col min-h-0 overflow-hidden">
            <ul class="min-w-0 flex-1 space-y-0.5 overflow-y-auto max-h-44 py-0.5 pr-1 min-h-0">
              @for (seg of productLegend(); track seg.label) {
                <li>
                  <button type="button"
                    class="w-full flex items-center gap-1.5 text-[0.7rem] leading-tight rounded-md px-1 -mx-1 py-0.5 hover:bg-slate-50 cursor-pointer text-left"
                    (click)="openProductDetail(seg.label)"
                    [pTooltip]="'Ver desglose de ' + seg.label"
                    tooltipPosition="left">
                    <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                    <span class="text-slate-600 truncate">{{ seg.label }}</span>
                    <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                      {{ seg.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                    </span>
                  </button>
                </li>
              } @empty {
                <li class="text-xs text-slate-400">Sin datos en este periodo</li>
              }
            </ul>
            @if (productLegendHasMore()) {
              <button
                type="button"
                class="mt-2 shrink-0 w-full rounded-md border border-teal-200 bg-teal-50 px-2 py-1.5 text-xs font-medium text-teal-800 hover:bg-teal-100"
                (click)="loadMoreProducts()">
                Cargar más productos
                <span class="text-teal-600/80 font-normal">({{ productLegendRemaining() }})</span>
              </button>
            }
          </div>
        </div>
      </div>

      <div class="chart-panel bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-col">
        <div class="flex items-start justify-between gap-2 mb-1 shrink-0">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Por forma de pago
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.payment"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <div class="flex items-center gap-1 shrink-0"
               [class.pointer-events-none]="data()?.loading"
               [class.opacity-60]="data()?.loading">
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="prevPaymentPeriod()"
              [disabled]="!!data()?.loading || !canPrevPaymentPeriod()"
              aria-label="Periodo anterior">
              <i class="pi pi-chevron-left text-xs"></i>
            </button>
            <select
              class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 capitalize focus:outline-none focus:ring-2 focus:ring-teal-500"
              [ngModel]="paymentPeriodValue()"
              (ngModelChange)="onPaymentPeriodValueChange($event)"
              [disabled]="!!data()?.loading"
              aria-label="Seleccionar mes y año">
              @for (opt of productPeriodOptions(); track opt.value) {
                <option [ngValue]="opt.value">{{ opt.label }}</option>
              }
            </select>
            <button type="button"
              class="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
              (click)="nextPaymentPeriod()"
              [disabled]="!!data()?.loading || !canNextPaymentPeriod()"
              aria-label="Periodo siguiente">
              <i class="pi pi-chevron-right text-xs"></i>
            </button>
          </div>
        </div>
        <div class="flex items-stretch gap-2 min-h-0 flex-1 mt-2">
          <div class="chart-area relative shrink-0 w-24 h-24 self-center">
            <p-chart type="doughnut"
              [data]="paymentChart()"
              [options]="paymentDoughnutOptions"
              class="block h-full w-full" />
          </div>
          <div class="min-w-0 flex-1 flex flex-col min-h-0 overflow-hidden">
            <ul class="min-w-0 flex-1 space-y-0.5 overflow-y-auto max-h-44 py-0.5 pr-1 min-h-0">
              @for (seg of paymentLegend(); track seg.key) {
                <li>
                  <button type="button"
                    class="w-full flex items-center gap-1.5 text-[0.7rem] leading-tight rounded-md px-1 -mx-1 py-0.5 hover:bg-slate-50 cursor-pointer text-left"
                    (click)="openPaymentDetail(seg.key)"
                    [pTooltip]="'Ver desglose de ' + seg.label"
                    tooltipPosition="left">
                    <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                    <span class="text-slate-600 truncate">{{ seg.label }}</span>
                    <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                      {{ seg.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                    </span>
                  </button>
                </li>
              } @empty {
                <li class="text-xs text-slate-400">Sin datos en este periodo</li>
              }
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Top clientes: fila completa -->
    <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col max-h-[22rem]">
      <div class="px-3 py-2 border-b border-slate-100 shrink-0">
        <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
          Top clientes
          <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
            [pTooltip]="help.monthly"
            tooltipPosition="right"
            tooltipStyleClass="forecast-help-tooltip"
            [showDelay]="200"></i>
        </h2>
        <p class="text-[0.7rem] text-slate-500 mt-0.5 leading-tight">
          Ranking por cobrado · {{ data()?.yearB ?? yearB() }} vs {{ data()?.yearA ?? yearA() }}.
          Clic en el cliente para la ficha; en un monto para el desglose.
        </p>
      </div>
      <div class="overflow-auto flex-1 min-h-0 flex flex-col">
        <p-table [value]="topCustomers()" styleClass="top-customers-table p-datatable-sm" [paginator]="false">
          <ng-template pTemplate="header">
            <tr>
              <th class="text-left w-8">#</th>
              <th class="text-left">Cliente</th>
              <th class="text-right">Cobrado {{ data()?.yearB ?? yearB() }}</th>
              <th class="text-right hidden md:table-cell">Cobrado {{ data()?.yearA ?? yearA() }}</th>
              <th class="text-right">Dif.</th>
              <th class="text-right hidden sm:table-cell">Var.</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr class="hover:bg-slate-50/80 transition-colors">
              <td class="text-left tabular-nums text-slate-500">{{ row.rank }}</td>
              <td class="text-left">
                <button type="button"
                  class="inline-flex items-center gap-1.5 min-w-0 max-w-full text-left group"
                  (click)="openCustomerDetails(row.customerId)"
                  [pTooltip]="'Ver ficha del cliente'">
                  <i
                    class="text-slate-400 text-[0.7rem] shrink-0"
                    [class.pi]="true"
                    [class.pi-user]="row.customerType === 'PERSON'"
                    [class.pi-building]="row.customerType === 'COMPANY'"
                    [class.pi-question-circle]="row.customerType !== 'PERSON' && row.customerType !== 'COMPANY'"></i>
                  <span class="font-medium text-teal-700 group-hover:underline truncate block max-w-[9rem] lg:max-w-[14rem]"
                        [pTooltip]="row.customerName">{{ row.customerName }}</span>
                </button>
              </td>
              <td class="text-right whitespace-nowrap">
                <button type="button"
                  class="tabular-nums font-semibold text-emerald-700 hover:underline hover:bg-emerald-50 rounded px-1 py-0"
                  (click)="openCustomerCollectedDetail(row, 'B')"
                  [pTooltip]="'Desglose cobrado ' + (data()?.yearB ?? yearB())">
                  {{ row.yearBAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                </button>
              </td>
              <td class="text-right whitespace-nowrap hidden md:table-cell">
                <button type="button"
                  class="tabular-nums font-semibold text-slate-700 hover:underline hover:bg-slate-100 rounded px-1 py-0"
                  (click)="openCustomerCollectedDetail(row, 'A')"
                  [pTooltip]="'Desglose cobrado ' + (data()?.yearA ?? yearA())">
                  {{ row.yearAAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                </button>
              </td>
              <td class="text-right tabular-nums font-semibold whitespace-nowrap" [class]="diffClass(row.difference)">
                {{ row.difference | currency:'MXN':'symbol-narrow':'1.0-0' }}
              </td>
              <td class="text-right tabular-nums font-semibold whitespace-nowrap hidden sm:table-cell" [class]="diffClass(row.differencePercent)">
                <span class="inline-flex items-center justify-end gap-0.5">
                  <i
                    class="text-[0.65rem]"
                    [class.pi]="true"
                    [class.pi-arrow-up]="row.differencePercent > 0"
                    [class.pi-arrow-down]="row.differencePercent < 0"
                    [class.pi-minus]="row.differencePercent === 0"></i>
                  {{ formatPercent(row.differencePercent) }}
                </span>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center text-slate-400 py-8 text-sm">
                Sin cobros por cliente en {{ data()?.yearB ?? yearB() }}
              </td>
            </tr>
          </ng-template>
        </p-table>
        @if (topCustomersHasMore()) {
          <div class="px-3 py-2 border-t border-slate-100 shrink-0">
            <button
              type="button"
              class="w-full rounded-md border border-teal-200 bg-teal-50 px-2 py-1.5 text-xs font-medium text-teal-800 hover:bg-teal-100"
              (click)="loadMoreTopCustomers()">
              Cargar más clientes
              <span class="text-teal-600/80 font-normal">({{ topCustomersRemaining() }})</span>
            </button>
          </div>
        }
      </div>
    </div>

    <p-drawer
      [visible]="isCustomerDetailsOpen()"
      position="right"
      (visibleChange)="onCustomerDrawerVisibleChange($event)"
      styleClass="customer-details-drawer"
      [blockScroll]="true">
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 shadow-sm">
              <i class="pi pi-user text-base"></i>
            </span>
            <h3 class="text-base font-semibold text-slate-900 truncate">Detalles del cliente</h3>
          </div>
          @if (selectedCustomer() && !customerDetailsLoading()) {
            <button
              type="button"
              class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
              (click)="editCustomerFromDrawer()">
              <i class="pi pi-pencil text-sm"></i>
              Editar
            </button>
          }
        </div>
      </ng-template>
      @if (customerDetailsLoading()) {
        <div class="flex flex-col items-center justify-center py-12 gap-4">
          <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
          <p class="text-sm text-slate-600">Cargando cliente...</p>
        </div>
      } @else if (selectedCustomer()) {
        <app-customer-read
          [customer]="selectedCustomer()!"
          (openSale)="openSaleFromCustomer($event)" />
      }
    </p-drawer>

    <p-drawer
      [visible]="isSaleDetailsOpen()"
      position="right"
      (visibleChange)="onSaleDrawerVisibleChange($event)"
      styleClass="sale-details-drawer"
      [blockScroll]="true">
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 shadow-sm">
              <i class="pi pi-shopping-cart text-base"></i>
            </span>
            <h3 class="text-base font-semibold text-slate-900 truncate">Detalle de venta</h3>
          </div>
          @if (selectedSale() && !saleDetailsLoading()) {
            <button
              type="button"
              class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
              (click)="editSaleFromDrawer()">
              <i class="pi pi-pencil text-sm"></i>
              Editar
            </button>
          }
        </div>
      </ng-template>
      @if (saleDetailsLoading()) {
        <div class="flex flex-col items-center justify-center py-12 gap-4">
          <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
          <p class="text-sm text-slate-600">Cargando detalle...</p>
        </div>
      } @else if (selectedSale()) {
        <app-sale-read
          [sale]="selectedSale()!"
          [charges]="saleCharges()"
          (openCustomer)="openCustomerFromSale($event)" />
      }
    </p-drawer>

    <p-drawer
      [visible]="isCardDetailOpen()"
      position="right"
      (visibleChange)="onCardDrawerVisibleChange($event)"
      styleClass="card-detail-drawer"
      [blockScroll]="true">
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full gap-3 min-w-0">
          <div class="flex items-center gap-3 min-w-0">
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-600 shadow-sm">
              <i class="pi pi-wallet text-base"></i>
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-slate-900 truncate">
                {{ cardDetail()?.title || 'Desglose' }}
              </h3>
              @if (cardDetail(); as detail) {
                <p class="text-xs text-slate-500 mt-0.5">
                  {{ detail.count }} {{ detail.subtitle }}
                  ·
                  <span class="font-semibold tabular-nums text-slate-800">
                    {{ detail.total | currency:'MXN':'symbol-narrow':'1.0-0' }}
                  </span>
                </p>
              }
            </div>
          </div>
          @if (cardDetail(); as detail) {
            @if (!detail.loading && !detail.error) {
              <span
                class="inline-flex shrink-0"
                [pTooltip]="cardExportTooltip()"
                tooltipPosition="left"
                appendTo="body">
                <p-button
                  label="Exportar CSV"
                  icon="pi pi-download"
                  severity="secondary"
                  size="small"
                  [rounded]="true"
                  [loading]="exporting()"
                  [disabled]="!canExportCharges || exporting() || detail.items.length === 0"
                  (onClick)="exportCardDetailCsv()" />
              </span>
            }
          }
        </div>
      </ng-template>
      @if (cardDetail(); as detail) {
        @if (detail.loading) {
          <div class="flex flex-col items-center justify-center py-12 gap-4">
            <i class="pi pi-spin pi-spinner text-4xl text-teal-500"></i>
            <p class="text-sm text-slate-600">Cargando desglose...</p>
          </div>
        } @else if (detail.error) {
          <div class="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm">
            {{ detail.error }}
          </div>
        } @else {
          <p-table
            [value]="detail.items"
            dataKey="key"
            [paginator]="true"
            [rows]="cardDetailPageSize"
            [rowsPerPageOptions]="[10, 20, 50]"
            [first]="cardDetailTableFirst()"
            sortMode="single"
            [resetPageOnSort]="true"
            [rowHover]="true"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} {{ cardDetail()?.card === 'NEXT_MONTH_PROJECTION' ? 'cobros' : 'pagos' }}"
            styleClass="card-detail-table p-datatable-sm"
            paginatorStyleClass="!border-0 !bg-transparent !pt-4"
            (firstChange)="cardDetailTableFirst.set($event)"
            (onSort)="onCardDetailSort()"
            (onPage)="onCardDetailPage($event)">
            <ng-template pTemplate="header">
              <tr>
                <th class="text-left w-[4.75rem]" pSortableColumn="index">
                  # <p-sortIcon field="index" />
                </th>
                <th class="text-left" pSortableColumn="customerName">
                  Cliente <p-sortIcon field="customerName" />
                </th>
                <th class="text-left hidden xl:table-cell w-[22%]" pSortableColumn="description">
                  Notas <p-sortIcon field="description" />
                </th>
                <th class="text-center hidden 2xl:table-cell w-14" pSortableColumn="paymentNumber">
                  Pago <p-sortIcon field="paymentNumber" />
                </th>
                <th class="text-center w-[7.5rem]" [pSortableColumn]="cardDetailDateSortField()">
                  {{ cardDetailDateHeader() }} <p-sortIcon [field]="cardDetailDateSortField()" />
                </th>
                <th class="text-center hidden lg:table-cell w-[5.5rem]" pSortableColumn="status">
                  Estado <p-sortIcon field="status" />
                </th>
                <th class="text-right w-[5.5rem]" pSortableColumn="total">
                  Monto <p-sortIcon field="total" />
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="text-left whitespace-nowrap">
                  <div class="flex items-center gap-1">
                    <span class="shrink-0 h-6 w-6 rounded-full flex items-center justify-center"
                      [class.bg-teal-50]="row.source === 'CHARGE' || row.source === 'PAYMENT'"
                      [class.text-teal-600]="row.source === 'CHARGE' || row.source === 'PAYMENT'"
                      [class.bg-violet-50]="row.source === 'SALE'"
                      [class.text-violet-600]="row.source === 'SALE'"
                      [class.bg-emerald-50]="row.source === 'SUBSCRIPTION'"
                      [class.text-emerald-600]="row.source === 'SUBSCRIPTION'">
                      <i class="text-xs"
                        [class.pi]="true"
                        [class.pi-wallet]="row.source === 'CHARGE' || row.source === 'PAYMENT' || !row.source"
                        [class.pi-shopping-cart]="row.source === 'SALE'"
                        [class.pi-replay]="row.source === 'SUBSCRIPTION'"></i>
                    </span>
                    <span class="tabular-nums text-slate-500 font-medium shrink-0">{{ row.index }}</span>
                  </div>
                </td>
                <td class="text-left">
                  @if (row.customerId) {
                    <button type="button"
                      class="inline-flex items-center gap-1 min-w-0 max-w-full text-left group"
                      (click)="openCustomerDetails(row.customerId)"
                      [pTooltip]="'Ver ficha del cliente'">
                      <i
                        class="text-slate-400 text-[0.65rem] shrink-0"
                        [class.pi]="true"
                        [class.pi-user]="row.customerType === 'PERSON'"
                        [class.pi-building]="row.customerType === 'COMPANY'"
                        [class.pi-question-circle]="row.customerType !== 'PERSON' && row.customerType !== 'COMPANY'"></i>
                      <span class="font-medium text-teal-700 group-hover:underline truncate"
                            [pTooltip]="row.customerName">{{ row.customerName }}</span>
                    </button>
                  } @else {
                    <span class="text-slate-500 truncate block" [pTooltip]="row.customerName">{{ row.customerName }}</span>
                  }
                </td>
                <td class="text-left hidden xl:table-cell w-[22%]">
                  <div class="min-w-0">
                    @if (row.saleId) {
                      <button type="button"
                        class="font-semibold text-teal-700 hover:underline text-left"
                        (click)="openSaleDetails(row.saleId, $event)">
                        {{ row.saleNumber || ('#' + row.saleId) }}
                      </button>
                    } @else if (row.source === 'SUBSCRIPTION') {
                      <span class="font-semibold text-emerald-700">{{ row.saleNumber || 'Suscripción' }}</span>
                    }
                    <p class="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[0.65rem] text-slate-500"
                      [pTooltip]="row.description && row.description !== '—' ? row.description : ''"
                      [tooltipDisabled]="!row.description || row.description === '—'"
                      tooltipPosition="top"
                      appendTo="body">
                      {{ row.description }}
                    </p>
                  </div>
                </td>
                <td class="text-center tabular-nums text-slate-500 hidden 2xl:table-cell">
                  @if (row.paymentNumber != null && row.numberOfPayments != null) {
                    {{ row.paymentNumber }}/{{ row.numberOfPayments }}
                  } @else {
                    —
                  }
                </td>
                <td class="text-center text-slate-500 tabular-nums whitespace-nowrap">
                  @if (detail.dateKind === 'PAID_AT') {
                    {{ row.paidAt ? (row.paidAt | date:'dd/MM/yyyy HH:mm') : '—' }}
                  } @else {
                    {{ row.dueDate ? (row.dueDate | date:'dd/MM/yyyy') : '—' }}
                  }
                </td>
                <td class="text-center hidden lg:table-cell">
                  <p-tag class="!text-[0.65rem]"
                    [value]="cardChargeStatusLabel(row.status)"
                    [severity]="cardChargeStatusSeverity(row.status)" />
                </td>
                <td class="text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
                  {{ row.total | currency:'MXN':'symbol-narrow':'1.0-0' }}
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7" class="text-center text-slate-400 py-10 text-xs">
                  {{ cardDetail()?.card === 'NEXT_MONTH_PROJECTION' ? 'Sin cobros en esta proyección' : 'Sin pagos en este desglose' }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }
    </p-drawer>

    <p-dialog
      [header]="'Cobrado · ' + (customerDetail()?.customerName || 'Cliente')"
      [visible]="customerDetailOpen()"
      (visibleChange)="onCustomerDetailVisible($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(56rem, 96vw)' }"
      [contentStyle]="{ paddingTop: '0.75rem' }"
      styleClass="customer-cashflow-detail-dialog">
      @if (customerDetail(); as detail) {
        <div class="space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              <button type="button"
                class="px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
                [class.bg-white]="customerDetailYearTab() === 'B'"
                [class.text-emerald-700]="customerDetailYearTab() === 'B'"
                [class.shadow-sm]="customerDetailYearTab() === 'B'"
                [class.text-slate-500]="customerDetailYearTab() !== 'B'"
                (click)="setCustomerDetailYearTab('B')">
                {{ detail.yearB }}
                <span class="ml-1 tabular-nums font-normal text-slate-400">
                  ({{ detail.yearBTotal | currency:'MXN':'symbol-narrow':'1.0-0' }})
                </span>
              </button>
              <button type="button"
                class="px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
                [class.bg-white]="customerDetailYearTab() === 'A'"
                [class.text-slate-800]="customerDetailYearTab() === 'A'"
                [class.shadow-sm]="customerDetailYearTab() === 'A'"
                [class.text-slate-500]="customerDetailYearTab() !== 'A'"
                (click)="setCustomerDetailYearTab('A')">
                {{ detail.yearA }}
                <span class="ml-1 tabular-nums font-normal text-slate-400">
                  ({{ detail.yearATotal | currency:'MXN':'symbol-narrow':'1.0-0' }})
                </span>
              </button>
            </div>
            <p class="text-xs text-slate-500">
              Pagos aplicados en {{ customerDetailYearLabel() }} ·
              total
              <span class="font-semibold text-slate-800 tabular-nums">
                {{ customerDetailYearTotal() | currency:'MXN':'symbol-narrow':'1.0-0' }}
              </span>
            </p>
            <span
              class="inline-flex shrink-0"
              [pTooltip]="customerDetailExportTooltip()"
              tooltipPosition="left"
              appendTo="body">
              <p-button
                label="Exportar CSV"
                icon="pi pi-download"
                severity="secondary"
                size="small"
                [rounded]="true"
                [loading]="exporting()"
                [disabled]="!canExportCharges || exporting() || customerDetailSales().length === 0 || detail.loading"
                (onClick)="exportCustomerDetailCsv()" />
            </span>
          </div>

          @if (detail.loading) {
            <div class="flex items-center justify-center gap-2 py-12 text-slate-500 text-sm">
              <i class="pi pi-spin pi-spinner"></i>
              Cargando desglose…
            </div>
          } @else if (detail.error) {
            <div class="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm">
              {{ detail.error }}
            </div>
          } @else {
            <p-table
              [value]="customerDetailSales()"
              dataKey="key"
              [expandedRowKeys]="expandedSaleKeys()"
              sortMode="single"
              styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th class="w-10"></th>
                  <th class="text-left text-xs" pSortableColumn="saleNumber">
                    Venta <p-sortIcon field="saleNumber" />
                  </th>
                  <th class="text-left hidden sm:table-cell text-xs" pSortableColumn="saleType">
                    Tipo <p-sortIcon field="saleType" />
                  </th>
                  <th class="text-right text-xs" pSortableColumn="chargeCount">
                    Cargos <p-sortIcon field="chargeCount" />
                  </th>
                  <th class="text-right text-xs" pSortableColumn="amount">
                    Cobrado <p-sortIcon field="amount" />
                  </th>
                  <th class="text-center hidden md:table-cell text-xs" pSortableColumn="lastPaidAt">
                    Último pago <p-sortIcon field="lastPaidAt" />
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-sale let-expanded="expanded">
                <tr>
                  <td>
                    <button type="button"
                      class="w-6 h-6 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                      (click)="toggleSaleExpand(sale, $event)"
                      [attr.aria-expanded]="!!expanded"
                      aria-label="Ver cargos">
                      <i class="pi text-[0.65rem]" [class.pi-chevron-right]="!expanded" [class.pi-chevron-down]="!!expanded"></i>
                    </button>
                  </td>
                  <td class="text-left text-xs">
                    <div class="min-w-0">
                      @if (sale.saleId) {
                        <button type="button"
                          class="font-semibold text-teal-700 hover:underline text-left"
                          (click)="openSaleDetails(sale.saleId, $event)">
                          {{ sale.saleNumber || ('#' + sale.saleId) }}
                        </button>
                      } @else {
                        <span class="font-semibold text-slate-700">Sin venta</span>
                      }
                      <p class="text-[0.7rem] text-slate-500 truncate max-w-[18rem]" [pTooltip]="sale.description">
                        {{ sale.description }}
                      </p>
                    </div>
                  </td>
                  <td class="text-left text-xs text-slate-600 hidden sm:table-cell">
                    {{ saleTypeLabel(sale.saleType) }}
                  </td>
                  <td class="text-right tabular-nums text-slate-600 text-xs">{{ sale.chargeCount }}</td>
                  <td class="text-right tabular-nums font-semibold text-slate-900 text-xs">
                    {{ sale.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                  </td>
                  <td class="text-center text-xs text-slate-500 tabular-nums hidden md:table-cell">
                    {{ sale.lastPaidAt ? (sale.lastPaidAt | date:'dd/MM/yyyy HH:mm') : '—' }}
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="rowexpansion" let-sale>
                <tr>
                  <td colspan="6" class="bg-slate-50/80 !p-0">
                    <div class="px-4 py-3">
                      <p class="text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                        Pagos de esta venta
                      </p>
                      <div class="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table class="w-full text-xs">
                          <thead class="bg-slate-50 text-slate-500">
                            <tr>
                              <th class="text-left px-3 py-2 font-medium">Descripción</th>
                              <th class="text-center px-3 py-2 font-medium">Pago</th>
                              <th class="text-center px-3 py-2 font-medium">Fecha</th>
                              <th class="text-right px-3 py-2 font-medium">Monto</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (ch of sale.charges; track ch.chargeId) {
                              <tr class="border-t border-slate-100">
                                <td class="text-left px-3 py-2 text-slate-700">{{ ch.description }}</td>
                                <td class="text-center px-3 py-2 tabular-nums text-slate-500">
                                  @if (ch.paymentNumber != null && ch.numberOfPayments != null) {
                                    {{ ch.paymentNumber }}/{{ ch.numberOfPayments }}
                                  } @else {
                                    —
                                  }
                                </td>
                                <td class="text-center px-3 py-2 tabular-nums text-slate-500">
                                  {{ ch.paidAt ? (ch.paidAt | date:'dd/MM/yyyy HH:mm') : '—' }}
                                </td>
                                <td class="text-right px-3 py-2 tabular-nums font-semibold text-slate-800">
                                  {{ ch.total | currency:'MXN':'symbol-narrow':'1.0-0' }}
                                </td>
                              </tr>
                            } @empty {
                              <tr>
                                <td colspan="4" class="px-3 py-4 text-center text-slate-400">Sin cargos</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6" class="text-center text-slate-400 py-10 text-xs">
                    Sin pagos aplicados en {{ customerDetailYearLabel() }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </div>
      }
    </p-dialog>

    <!-- Glosario -->
    <footer class="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button type="button"
        class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        (click)="toggleGlossary()"
        [attr.aria-expanded]="glossaryOpen()">
        <div>
          <h2 class="text-sm font-semibold text-slate-900">Términos usados</h2>
          <p class="mt-0.5 text-xs text-slate-500">
            Definiciones cortas de las métricas que aparecen en esta pantalla.
          </p>
        </div>
        <i class="pi text-slate-400 text-sm shrink-0"
          [class.pi-chevron-down]="!glossaryOpen()"
          [class.pi-chevron-up]="glossaryOpen()"></i>
      </button>
      @if (glossaryOpen()) {
        <dl class="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-3">
          @for (item of glossary; track item.term) {
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-teal-700">{{ item.term }}</dt>
              <dd class="mt-0.5 text-xs leading-snug text-slate-600">{{ item.definition }}</dd>
            </div>
          }
        </dl>
      }
    </footer>

  </div>
</div>
